import { forwardRef, useCallback, useImperativeHandle, useRef, type HTMLAttributes, type TdHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../../utils';
import { ArrowUp, ArrowDown, ArrowUpDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';

export type DataTableDensity = 'comfortable' | 'compact';

export interface DataTableProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'bordered' | 'striped';
  /**
   * Row density: 'comfortable' (40px rows, default) or 'compact' (28px rows).
   * Compact mode reduces vertical padding and uses smaller text.
   */
  density?: DataTableDensity;
}

const DataTable = forwardRef<HTMLDivElement, DataTableProps>(
  // `variant` is taken out of props so it is never forwarded to the <div>.
  ({ className, variant: _variant, density = 'comfortable', children, ...props }, ref) => (
    <div
      ref={ref}
      data-density={density}
      className={cn(
        'w-full overflow-hidden rounded-lg border border-border',
        density === 'compact' && 'data-table-compact',
        className
      )}
      {...props}
    >
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full align-middle">
          {children}
        </div>
      </div>
    </div>
  )
);
DataTable.displayName = 'DataTable';

export interface DataTableTableProps extends HTMLAttributes<HTMLTableElement> {}

const DataTableTable = forwardRef<HTMLTableElement, DataTableTableProps>(
  ({ className, ...props }, ref) => (
    <table
      ref={ref}
      className={cn('min-w-full divide-y divide-border', className)}
      {...props}
    />
  )
);
DataTableTable.displayName = 'DataTableTable';

export interface DataTableHeaderProps extends HTMLAttributes<HTMLTableSectionElement> {}

const DataTableHeader = forwardRef<HTMLTableSectionElement, DataTableHeaderProps>(
  ({ className, ...props }, ref) => (
    <thead
      ref={ref}
      className={cn('bg-[var(--table-band)]', className)}
      {...props}
    />
  )
);
DataTableHeader.displayName = 'DataTableHeader';

export interface DataTableBodyProps extends HTMLAttributes<HTMLTableSectionElement> {}

const DataTableBody = forwardRef<HTMLTableSectionElement, DataTableBodyProps>(
  ({ className, ...props }, ref) => (
    <tbody
      ref={ref}
      className={cn('divide-y bg-[var(--surface)]', className)}
      {...props}
    />
  )
);
DataTableBody.displayName = 'DataTableBody';

export interface DataTableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  isSelected?: boolean;
  isClickable?: boolean;
}

const DataTableRow = forwardRef<HTMLTableRowElement, DataTableRowProps>(
  ({ className, isSelected, isClickable, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        isClickable && 'cursor-pointer hover:bg-surface-hover',
        isSelected && 'bg-[var(--surface-selected)]',
        className
      )}
      {...props}
    />
  )
);
DataTableRow.displayName = 'DataTableRow';

export interface DataTableHeadProps extends HTMLAttributes<HTMLTableCellElement> {
  sortable?: boolean;
  sorted?: 'asc' | 'desc' | false;
  align?: 'left' | 'center' | 'right';
}

const DataTableHead = forwardRef<HTMLTableCellElement, DataTableHeadProps>(
  ({ className, sortable, sorted, align = 'left', children, ...props }, ref) => (
    <th
      ref={ref}
      scope="col"
      className={cn(
        // agn-dt-head and the agn-dt-head-sort-* glyph classes are inert hooks:
        // next-components.css (next scope only) takes the ink from semantic
        // tokens. The next DataTableHead twin owns the full .agn-dt-th chrome.
        'agn-dt-head px-4 py-3 text-xs font-medium uppercase tracking-wider text-text-muted',
        align === 'left' && 'text-left',
        align === 'center' && 'text-center',
        align === 'right' && 'text-right',
        sortable && 'agn-dt-head--sortable cursor-pointer select-none hover:text-text-primary',
        className
      )}
      {...props}
    >
      <div className={cn(
        'flex items-center gap-1.5',
        align === 'center' && 'justify-center',
        align === 'right' && 'justify-end',
      )}>
        {children}
        {sortable && (
          <span className="inline-flex" aria-hidden="true">
            {sorted === 'asc' ? (
              <ArrowUp className="agn-dt-head-sort-active h-3 w-3 text-text-secondary" />
            ) : sorted === 'desc' ? (
              <ArrowDown className="agn-dt-head-sort-active h-3 w-3 text-text-secondary" />
            ) : (
              <ArrowUpDown className="agn-dt-head-sort-idle h-3 w-3 text-text-placeholder" />
            )}
          </span>
        )}
      </div>
    </th>
  )
);
DataTableHead.displayName = 'DataTableHead';

export interface DataTableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  align?: 'left' | 'center' | 'right';
}

const DataTableCell = forwardRef<HTMLTableCellElement, DataTableCellProps>(
  ({ className, align = 'left', ...props }, ref) => (
    <td
      ref={ref}
      className={cn(
        'whitespace-nowrap px-4 py-3 text-sm text-text-primary',
        align === 'center' && 'text-center',
        align === 'right' && 'text-right',
        className
      )}
      {...props}
    />
  )
);
DataTableCell.displayName = 'DataTableCell';

export interface DataTableEmptyProps extends HTMLAttributes<HTMLTableCellElement> {
  colSpan?: number;
  message?: string;
  icon?: ReactNode;
}

const DataTableEmpty = forwardRef<HTMLTableCellElement, DataTableEmptyProps>(
  ({ className, colSpan, message = 'No data available', icon, ...props }, ref) => (
    <tr>
      <td
        ref={ref}
        colSpan={colSpan}
        className={cn('px-4 py-12 text-center', className)}
        {...props}
      >
        <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
          {icon && <div className="text-[var(--border-hover)]">{icon}</div>}
          <p className="text-sm">{message}</p>
        </div>
      </td>
    </tr>
  )
);
DataTableEmpty.displayName = 'DataTableEmpty';

export interface DataTableSectionHeaderProps extends HTMLAttributes<HTMLDivElement> {}

const DataTableSectionHeader = forwardRef<HTMLDivElement, DataTableSectionHeaderProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'px-4 py-2 bg-[var(--table-band)] border-b border-border text-sm font-medium text-text-secondary',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
);
DataTableSectionHeader.displayName = 'DataTableSectionHeader';

export interface DataTableActionProps {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'default' | 'danger';
}

const DataTableAction = forwardRef<HTMLButtonElement, DataTableActionProps>(
  ({ icon, label, onClick, disabled, variant = 'default' }, ref) => (
    <Tooltip.Provider delayDuration={200}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <button
            ref={ref}
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={cn(
              'p-1.5 rounded-md transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-[color:var(--ring-color)] focus:ring-offset-1 focus:ring-offset-surface',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              variant === 'default' && [
                'text-muted-foreground hover:text-text-secondary hover:bg-surface-hover',
              ],
              variant === 'danger' && [
                'text-muted-foreground hover:text-error-600 hover:bg-error-50',
              ]
            )}
            aria-label={label}
          >
            {icon}
          </button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className="z-50 rounded-md bg-[var(--action-primary)] px-2 py-1 text-xs text-[var(--action-primary-fg)] shadow-md"
            sideOffset={5}
          >
            {label}
            <Tooltip.Arrow className="fill-[var(--action-primary)]" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  )
);
DataTableAction.displayName = 'DataTableAction';

export interface DataTableActionsProps extends HTMLAttributes<HTMLDivElement> {}

const DataTableActions = forwardRef<HTMLDivElement, DataTableActionsProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-center gap-0.5', className)}
      {...props}
    >
      {children}
    </div>
  )
);
DataTableActions.displayName = 'DataTableActions';

/**
 * Optional translated labels for the pagination control. The design system is
 * intentionally i18n-agnostic, so each label defaults to English; consumers pass
 * translated strings from their own i18n layer (e.g. react-i18next `t()`).
 */
export interface DataTableFooterProps extends HTMLAttributes<HTMLDivElement> {}

/**
 * Footer slot of a table card. Render it as the LAST child of <DataTable>
 * (or any rounded container) and put <DataTablePagination> — plus any other
 * footer content such as bulk-action bars or totals — inside it.
 *
 * It guarantees the card's bottom corner rounding stays intact: the footer
 * inherits the parent's bottom radius and clips its children, so square
 * corners can never poke out of a rounded table card. Children carry their
 * own top border (DataTablePagination already does).
 */
const DataTableFooter = forwardRef<HTMLDivElement, DataTableFooterProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="table-footer"
      className={cn(
        'rounded-b-[inherit] overflow-hidden bg-[var(--surface)]',
        className
      )}
      {...props}
    />
  )
);
DataTableFooter.displayName = 'DataTableFooter';

export interface DataTablePaginationLabels {
  /** Separator between counts, e.g. "1 – 25 {of} 406" and "Page 1 {of} 17" */
  of?: string;
  /** Label preceding the current page number */
  page?: string;
  goToFirstPage?: string;
  goToPreviousPage?: string;
  goToNextPage?: string;
  goToLastPage?: string;
  /** aria-label builder for a numbered page button */
  goToPage?: (page: number) => string;
  /** Label for the page-size selector (shown when pageSizeOptions is provided) */
  rowsPerPage?: string;
}

const DEFAULT_PAGINATION_LABELS: Required<DataTablePaginationLabels> = {
  of: 'of',
  page: 'Page',
  goToFirstPage: 'Go to first page',
  goToPreviousPage: 'Go to previous page',
  goToNextPage: 'Go to next page',
  goToLastPage: 'Go to last page',
  goToPage: (page: number) => `Go to page ${page}`,
  rowsPerPage: 'Rows per page',
};

export interface DataTablePaginationProps extends HTMLAttributes<HTMLDivElement> {
  currentPage?: number;
  totalPages?: number;
  pageSize?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  /** Number of page links to show around current page */
  siblingCount?: number;
  /** Optional translated labels; English defaults when omitted */
  labels?: DataTablePaginationLabels;
  /**
   * Control layout. 'numbered' (default) shows first/prev/[page numbers]/next/last
   * for random page access. 'simple' shows only Prev/Next — the fit-for-purpose
   * choice for offset-paginated lists where deep random access isn't needed.
   */
  variant?: 'numbered' | 'simple';
  /**
   * When provided alongside onPageSizeChange, renders a page-size selector.
   * The current pageSize is always included so the selector never shows an
   * empty value even if pageSize isn't one of the options.
   */
  pageSizeOptions?: number[];
}

function generatePagination(currentPage: number, totalPages: number, siblingCount: number = 1) {
  const pages: (number | 'ellipsis')[] = [];

  // Always show first page
  pages.push(1);

  // Calculate range around current page
  const leftSibling = Math.max(2, currentPage - siblingCount);
  const rightSibling = Math.min(totalPages - 1, currentPage + siblingCount);

  // Add left ellipsis if needed
  if (leftSibling > 2) {
    pages.push('ellipsis');
  }

  // Add pages around current
  for (let i = leftSibling; i <= rightSibling; i++) {
    if (i !== 1 && i !== totalPages) {
      pages.push(i);
    }
  }

  // Add right ellipsis if needed
  if (rightSibling < totalPages - 1) {
    pages.push('ellipsis');
  }

  // Always show last page (if more than 1 page)
  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages;
}

interface PaginationButtonProps {
  page?: number;
  children: ReactNode;
  disabled?: boolean;
  active?: boolean;
  'aria-label'?: string;
  onSelect: (page: number) => void;
}

// Module scope keeps the component type stable across renders; declared inside
// DataTablePagination it remounted every page button on each parent render,
// dropping keyboard focus and detaching a button between query and click.
function PaginationButton({
  page,
  children,
  disabled,
  active,
  'aria-label': ariaLabel,
  onSelect,
}: PaginationButtonProps) {
  return (
    <button
      type="button"
      onClick={() => page && onSelect(page)}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex items-center justify-center min-w-[32px] h-8 px-2 text-sm rounded-md transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-[color:var(--ring-color)] focus:ring-offset-1 focus:ring-offset-surface',
        active
          ? 'bg-[var(--action-primary)] text-[var(--action-primary-fg)]'
          : 'text-text-muted hover:bg-surface-hover',
        disabled && 'opacity-50 cursor-not-allowed hover:bg-transparent'
      )}
    >
      {children}
    </button>
  );
}

const DataTablePagination = forwardRef<HTMLDivElement, DataTablePaginationProps>(
  ({
    className,
    currentPage = 1,
    totalPages = 1,
    pageSize = 10,
    totalItems = 0,
    onPageChange,
    onPageSizeChange,
    siblingCount = 1,
    labels,
    variant = 'numbered',
    pageSizeOptions,
    ...props
  }, ref) => {
    const t = { ...DEFAULT_PAGINATION_LABELS, ...labels };
    // An empty result still spans "page 1 of 1"; clamp the start row to 0 so
    // the count reads "0 – 0 of 0" rather than "1 – 0 of 0".
    const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);
    const pages = generatePagination(currentPage, totalPages, siblingCount);

    const showPageSizeSelect = Boolean(
      onPageSizeChange && pageSizeOptions && pageSizeOptions.length > 0,
    );
    // Always include the active pageSize so the <select> reflects the real
    // state even if a caller passes a pageSize outside its options list.
    const sizeOptions =
      showPageSizeSelect && !pageSizeOptions!.includes(pageSize)
        ? [...pageSizeOptions!, pageSize].sort((a, b) => a - b)
        : pageSizeOptions ?? [];

    // Local ref so we can scroll the table card back into view on page
    // change. Without this, a user scrolled down to the pagination
    // control clicks "Next" and stays in the same scroll position —
    // the data behind them changed but they're still looking at the
    // OLD page's tail rows (or empty space if the new page is shorter).
    // Reported as "page navigation doesn't take me to page 2" on
    // /intelligence/resolution. Forwarding the ref through
    // useImperativeHandle keeps the public API intact while letting us
    // own a private inner ref for the scroll gesture.
    const innerRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => innerRef.current as HTMLDivElement);

    const handlePageChange = useCallback(
      (page: number) => {
        if (page === currentPage) return;
        onPageChange?.(page);
        // Defer the scroll to the next frame so the table data has a
        // chance to start updating; we want the new page's first row
        // to be at (or near) the top of the viewport when the scroll
        // settles. Smooth so the change is observed, not jarring.
        const node = innerRef.current?.parentElement ?? innerRef.current;
        if (!node) return;
        // Only scroll if the table top is currently above the viewport.
        // For tables that fit on-screen there's nothing to do — and a
        // forced scroll there would be noise.
        requestAnimationFrame(() => {
          const rect = node.getBoundingClientRect();
          if (rect.top < 0) {
            node.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      },
      [currentPage, onPageChange],
    );

    return (
      <div
        ref={innerRef}
        className={cn(
          // rounded-b-[inherit] keeps the parent card's bottom corner rounding
          // intact when the pagination is its last child and the parent does
          // not clip (no overflow-hidden). Inside <DataTable> it is a no-op.
          'flex items-center justify-between border-t bg-[var(--surface)] px-4 py-3 rounded-b-[inherit]',
          className
        )}
        {...props}
      >
        {/* Page-size selector + items count (+ page position for numbered) */}
        <div className="flex items-center gap-4 text-sm text-text-muted">
          {showPageSizeSelect && (
            <label className="flex items-center gap-2">
              <span>{t.rowsPerPage}</span>
              <select
                value={pageSize}
                onChange={(event) => onPageSizeChange?.(Number(event.target.value))}
                aria-label={t.rowsPerPage}
                className={cn(
                  'h-8 rounded-md border border-border bg-[var(--surface)] px-2 text-sm text-text-secondary',
                  'focus:outline-none focus:ring-2 focus:ring-[color:var(--ring-color)] focus:ring-offset-1 focus:ring-offset-surface',
                )}
              >
                {sizeOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </label>
          )}
          <div>
            <span className="font-medium">{startItem}</span>
            {' '}&ndash;{' '}
            <span className="font-medium">{endItem}</span>
            {' '}{t.of}{' '}
            <span className="font-medium">{totalItems}</span>
            {variant === 'numbered' && (
              <>
                <span className="mx-2 text-[var(--border-hover)]">·</span>
                {t.page}{' '}
                <span className="font-medium">{currentPage}</span>
                {' '}{t.of}{' '}
                <span className="font-medium">{totalPages}</span>
              </>
            )}
          </div>
        </div>

        {/* Pagination controls */}
        {variant === 'simple' ? (
          <div className="flex items-center gap-1">
            <PaginationButton
              onSelect={handlePageChange}
              page={currentPage - 1}
              disabled={currentPage === 1}
              aria-label={t.goToPreviousPage}
            >
              <ChevronLeft className="h-4 w-4" />
            </PaginationButton>
            <PaginationButton
              onSelect={handlePageChange}
              page={currentPage + 1}
              disabled={currentPage >= totalPages}
              aria-label={t.goToNextPage}
            >
              <ChevronRight className="h-4 w-4" />
            </PaginationButton>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            {/* First page */}
            <PaginationButton
              onSelect={handlePageChange}
              page={1}
              disabled={currentPage === 1}
              aria-label={t.goToFirstPage}
            >
              <ChevronsLeft className="h-4 w-4" />
            </PaginationButton>

            {/* Previous page */}
            <PaginationButton
              onSelect={handlePageChange}
              page={currentPage - 1}
              disabled={currentPage === 1}
              aria-label={t.goToPreviousPage}
            >
              <ChevronLeft className="h-4 w-4" />
            </PaginationButton>

            {/* Page numbers */}
            <div className="flex items-center gap-1 mx-1">
              {pages.map((page, index) =>
                page === 'ellipsis' ? (
                  <span key={`ellipsis-${index}`} className="px-1 text-text-placeholder">
                    …
                  </span>
                ) : (
                  <PaginationButton
                    key={page}
                    onSelect={handlePageChange}
                    page={page}
                    active={page === currentPage}
                    aria-label={t.goToPage(page)}
                  >
                    {page}
                  </PaginationButton>
                )
              )}
            </div>

            {/* Next page */}
            <PaginationButton
              onSelect={handlePageChange}
              page={currentPage + 1}
              disabled={currentPage === totalPages}
              aria-label={t.goToNextPage}
            >
              <ChevronRight className="h-4 w-4" />
            </PaginationButton>

            {/* Last page */}
            <PaginationButton
              onSelect={handlePageChange}
              page={totalPages}
              disabled={currentPage === totalPages}
              aria-label={t.goToLastPage}
            >
              <ChevronsRight className="h-4 w-4" />
            </PaginationButton>
          </div>
        )}
      </div>
    );
  }
);
DataTablePagination.displayName = 'DataTablePagination';

export {
  DataTable,
  DataTableTable,
  DataTableHeader,
  DataTableBody,
  DataTableRow,
  DataTableHead,
  DataTableCell,
  DataTableEmpty,
  DataTableSectionHeader,
  DataTableAction,
  DataTableActions,
  DataTableFooter,
  DataTablePagination,
};
